import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const ribbonVariants = cva(
  "absolute z-20 flex items-center justify-center leading-none px-2 py-1 md:px-3 md:py-1.5 text-[9px] md:text-[10px] font-bold text-white shadow-sm transition-all duration-300 uppercase tracking-wide hover:brightness-110 hover:scale-105 select-none",
  {
    variants: {
      variant: {
        black: "bg-gradient-to-r from-gray-900 to-gray-700",
        primary: "bg-gradient-to-r from-primary-600 to-primary-400",
        secondary: "bg-white text-gray-900 border border-gray-100",
        success: "bg-gradient-to-r from-success-600 to-success-400",
        error: "bg-gradient-to-r from-error-600 to-error-400",
        warning: "bg-gradient-to-r from-warning-600 to-warning-400",
        info: "bg-gradient-to-r from-info-600 to-info-400",
      },
    },
    defaultVariants: {
      variant: "black",
    },
  }
)

const ribbonTailVariants = cva("absolute w-[6px] h-[6px] md:w-[8px] md:h-[8px] z-10", {
  variants: {
    variant: {
      black: "text-gray-900 brightness-50",
      primary: "text-primary-600 brightness-75",
      secondary: "text-gray-300 brightness-90",
      success: "text-success-600",
      error: "text-error-600",
      warning: "text-warning-600",
      info: "text-info-600",
    },
  },
  defaultVariants: {
    variant: "black",
  },
})

interface RibbonCardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof ribbonVariants> {
  ribbon?: string
  ribbonClassName?: string
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right"
}

const positionClasses = {
  "top-left": {
    container: "top-3 -left-[5px] md:top-4 md:-left-2",
    ribbon: "rounded-r-md rounded-tl-none left-0 top-0",
    tail: "top-full left-0",
    tailReflect: "scale-100",
  },
  "top-right": {
    container: "top-3 -right-[5px] md:top-4 md:-right-2",
    ribbon: "rounded-l-md rounded-tr-none right-0 top-0",
    tail: "top-full right-0",
    tailReflect: "scale-x-[-1]",
  },
  "bottom-left": {
    container: "bottom-3 -left-[5px] md:bottom-4 md:-left-2",
    ribbon: "rounded-r-md rounded-bl-none left-0 bottom-0",
    tail: "bottom-full left-0",
    tailReflect: "scale-y-[-1]",
  },
  "bottom-right": {
    container: "bottom-3 -right-[5px] md:bottom-4 md:-right-2",
    ribbon: "rounded-l-md rounded-br-none right-0 bottom-0",
    tail: "bottom-full right-0",
    tailReflect: "scale-x-[-1] scale-y-[-1]",
  },
}

const RibbonCard = React.forwardRef<HTMLDivElement, RibbonCardProps>(({ className, variant, ribbon, ribbonClassName, position = "top-left", children, ...props }, ref) => {
  const config = positionClasses[position] || positionClasses["top-left"]

  return (
    <div ref={ref} className={cn("relative group/ribbon rounded-lg border hover:scale-[1.01] transition-all duration-300 shadow-sm hover:shadow-md", "overflow-visible", className)} {...props}>
      {ribbon && (
        <div className={cn("absolute z-20", config.container)}>
          <div className={cn(ribbonVariants({ variant }), "relative", config.ribbon, ribbonClassName)}>{ribbon}</div>
          <svg className={cn(ribbonTailVariants({ variant }), config.tail, config.tailReflect)} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0 0 L100 0 L100 100 Z" fill="currentColor" />
          </svg>
        </div>
      )}

      {/* Content with responsive padding */}
      <div className="relative z-10 h-full w-full p-[16px]">{children}</div>

      <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none z-0 w-full">
        <img src="/assets/pattern-primary.svg" alt="pattern" className="absolute top-0 right-0" />
      </div>
    </div>
  )
})
RibbonCard.displayName = "RibbonCard"

export { RibbonCard }
